import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import CookieBanner from "@/components/CookieBanner";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";

import Privacy from "./pages/Privacy";
import Index from "./pages/Index";
import SupplierMaster from "./pages/SupplierMaster";
import CustomerMaster from "./pages/CustomerMaster";


import HistoricoFacturas from "./pages/HistoricoFacturas";
import AppShell from "./layouts/AppShell";
import RecibidasLayout from "./layouts/RecibidasLayout";
import EmitidasLayout from "./layouts/EmitidasLayout";
import ConvertirExcel from "./pages/emitidas/ConvertirExcel";
import ProcesarPdfEmitidas from "./pages/emitidas/ProcesarPdf";
import ConvertirExcelRecibidas from "./pages/recibidas/ConvertirExcel";
import RequireIssuedAccess from "./components/RequireIssuedAccess";
import RequireAdmin from "./components/RequireAdmin";
import RequireFinancing from "./components/RequireFinancing";
import RequirePayments from "./components/RequirePayments";
import AgendaPagos from "./pages/AgendaPagos";
import Bancos from "./pages/Bancos";
import FinanciacionLayout from "./layouts/FinanciacionLayout";
import Leasings from "./pages/financiacion/Leasings";
import Prestamos from "./pages/financiacion/Prestamos";

const LeasingRedirect = () => <Navigate to="/financiacion/leasings" replace />;
const CustomerRedirect = () => {
  const { companyId } = useParams();
  return <Navigate to={`/proveedores/${companyId}`} replace />;
};

import AdminPanel from "./pages/AdminPanel";
import AuditLog from "./pages/AuditLog";
import Dashboard from "./pages/Dashboard";
import Hub from "./pages/Hub";
import DatosEmpresa from "./pages/DatosEmpresa";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <div className="notranslate" translate="no" suppressHydrationWarning>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/privacidad" element={<Privacy />} />

                <Route path="/cambiar-contrasena" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
                <Route path="/hub" element={<ProtectedRoute><Hub /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/proveedores/:companyId" element={<ProtectedRoute><SupplierMaster /></ProtectedRoute>} />
                <Route path="/clientes" element={<ProtectedRoute><CustomerMaster /></ProtectedRoute>} />
                <Route path="/clientes/:companyId" element={<ProtectedRoute><CustomerRedirect /></ProtectedRoute>} />
                <Route path="/historico-facturas" element={<ProtectedRoute><HistoricoFacturas /></ProtectedRoute>} />
                <Route path="/datos-empresa" element={<ProtectedRoute><DatosEmpresa /></ProtectedRoute>} />

                <Route path="/facturas-emitidas" element={<Navigate to="/emitidas" replace />} />

                {/* Shell con sidebar: alberga los dos entornos principales */}
                <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                  {/* Recibidas: Desde PDF / Desde Excel */}
                  <Route path="/app" element={<RecibidasLayout />}>
                    <Route index element={<Navigate to="/app/pdf" replace />} />
                    <Route path="pdf" element={<Index />} />
                    <Route path="excel" element={<ConvertirExcelRecibidas />} />
                  </Route>

                  {/* Emitidas: por ahora sólo Desde Excel */}
                  <Route
                    path="/emitidas"
                    element={
                      <RequireIssuedAccess>
                        <EmitidasLayout />
                      </RequireIssuedAccess>
                    }
                  >
                    <Route index element={<Navigate to="/emitidas/pdf" replace />} />
                    <Route path="pdf" element={<ProcesarPdfEmitidas />} />
                    <Route path="excel" element={<ConvertirExcel />} />
                    <Route path="convertir" element={<Navigate to="/emitidas/excel" replace />} />
                  </Route>

                  <Route path="/pagos" element={<RequirePayments><AgendaPagos /></RequirePayments>} />
                  <Route path="/bancos" element={<RequireAdmin redirectTo="/hub"><Bancos /></RequireAdmin>} />
                  {/* Financiación: consulta de leasings y préstamos */}
                  <Route path="/financiacion" element={<RequireFinancing><FinanciacionLayout /></RequireFinancing>}>
                    <Route index element={<Navigate to="/financiacion/leasings" replace />} />
                    <Route path="leasings" element={<Leasings />} />
                    <Route path="prestamos" element={<Prestamos />} />
                  </Route>
                </Route>
                <Route path="/arrendamientos/:companyId" element={<ProtectedRoute><LeasingRedirect /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
                <Route path="/auditoria" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <CookieBanner />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </div>
);

App.displayName = "App";

export default App;
